import { isEmailValid } from '@/hooks';
import { AddMembersPayload } from '@/requests';
import { PlanLimits } from '@/store';

import { CSVRow, FormRow, InputKey } from '../types';

// Form Utils
export const createRow = () => ({
  id: crypto.randomUUID() as string,
  inputs: { email: '', name: '' },
  errors: { email: false, name: false },
});

const isNameValid = (name: string) => name.trim().length > 0;

export const validateForm = (form: FormRow[]) => {
  return form.map(row => ({
    ...row,
    errors: {
      email: !isEmailValid(row.inputs.email),
      name: !isNameValid(row.inputs.name),
    },
  }));
};

export const isFormDirty = (form: FormRow[]) => {
  return form.some(row =>
    Object.values(row.inputs).some(input => input !== ''),
  );
};

export const isFormInvalid = (form: FormRow[]) => {
  return form.some(row =>
    Object.values(row.errors).some(inputError => inputError === true),
  );
};

export const transformCSVToFormData = (data: CSVRow[]) => {
  const rows: FormRow[] = [];
  const fixableRows: FormRow[] = [];

  data.forEach((item, index) => {
    const emailError = !isEmailValid(item.email.trim());
    const nameError = !isNameValid(item.name);

    const row: FormRow = {
      ...createRow(),
      originRowNumber: index + 1,
      inputs: { email: item.email.trim(), name: item.name.trim() },
      errors: { email: emailError, name: nameError },
    };

    rows.push(row);

    if (emailError || nameError) {
      fixableRows.push(row);
    }
  });

  return {
    formData: rows,
    fixableRows,
  };
};

// List the rows in which a warning applies
// Warnings do not block form submission
// - Duplicates... Row 1
type WarningKey = 'duplicateEmails';
const warningMessages: Record<WarningKey, string> = {
  duplicateEmails: 'Duplicate emails will be excluded from the invite. ',
};
// List the rows in which an error occurred
// errors block form submission
// - Missing name. Row 1
// - Missing or invalid email address. Rows 2, 3
const errorMessages: Record<InputKey, string> = {
  name: 'Missing name. ',
  email: 'Missing or invalid email address. ',
};

export const getFormMessages = (form: FormRow[]) => {
  const errorRows: Record<InputKey, number[]> = {
    email: [],
    name: [],
  };
  const warningRows: Record<WarningKey, number[]> = {
    duplicateEmails: [],
  };

  const seenEmails = new Set<string>();
  form.forEach((row, index) => {
    const email = row.inputs.email.trim().toLowerCase();
    if (seenEmails.has(email)) {
      warningRows.duplicateEmails.push(
        row.originRowNumber ? row.originRowNumber : index + 1,
      );
    } else {
      seenEmails.add(email);
    }

    if (row.errors.email) {
      errorRows.email.push(
        row.originRowNumber ? row.originRowNumber : index + 1,
      );
    }
    if (row.errors.name) {
      errorRows.name.push(
        row.originRowNumber ? row.originRowNumber : index + 1,
      );
    }
  });

  const errors = Object.entries(errorRows)
    .filter(([_, rows]) => rows.length > 0)
    .map(
      ([key, rows]) =>
        errorMessages[key as InputKey] + getFormattedRowText(rows),
    );

  const warnings = Object.entries(warningRows)
    .filter(([_, rows]) => rows.length > 0)
    .map(
      ([key, rows]) =>
        warningMessages[key as WarningKey] + getFormattedRowText(rows),
    );

  return { errors, warnings };
};

const getFormattedRowText = (rows: number[]) => {
  return `Row${rows.length > 1 ? 's' : ''}: ${rows.join(', ')}`;
};

//Server error messages
type ErrorMessages = Record<string, { SINGLE: string; MULTIPLE: string }>;
const ADD_MEMBERS_ERROR_MESSAGES: ErrorMessages = {
  'lumiq.membership.authorization.duplicate_emails': {
    SINGLE: 'Account already exists',
    MULTIPLE: 'Some accounts already exist. Remove them and try again.',
  },
};

const AddMemberErrorCodes = Object.keys(ADD_MEMBERS_ERROR_MESSAGES);

export const getAddMemberErrorMessage = (
  error: any,
  newMembersCount: number,
) => {
  return error?.data?.details &&
    AddMemberErrorCodes.includes(error.data.details)
    ? newMembersCount === 1
      ? ADD_MEMBERS_ERROR_MESSAGES[error.data.details].SINGLE
      : ADD_MEMBERS_ERROR_MESSAGES[error.data.details].MULTIPLE
    : 'Something went wrong. Please try again or contact support';
};

export function transformFormDataToPayload(
  form: FormRow[],
  activeMemberEmailsSet: Set<string>,
) {
  const seenEmails = new Set<string>();
  const result: AddMembersPayload['newMembers'] = [];

  form.forEach(row => {
    const trimmedEmail = row.inputs.email.trim();
    const key = trimmedEmail.toLowerCase();

    if (!seenEmails.has(key)) {
      seenEmails.add(key);

      if (!activeMemberEmailsSet.has(key)) {
        result.push({
          email: trimmedEmail,
          name: row.inputs.name.trim(),
        });
      }
    }
  });

  return result;
}

export const mergeFixedFormRows = (
  form: FormRow[],
  csvSubsetForm: FormRow[],
) => {
  return form.map(
    row =>
      csvSubsetForm.find(r => r.originRowNumber === row.originRowNumber) ?? row,
  );
};

export const isUnderSeatCap = (
  planLimits: PlanLimits,
  newMemberCount: number,
  activeMemberCount: number,
) => {
  if (planLimits.seatCap === undefined) return true;
  if (planLimits.seatCap.enforce === false) return true;

  return activeMemberCount + newMemberCount <= planLimits.seatCap.seatCap;
};

export const isOnlyUniqueEmails = (data: { name: string; email: string }[]) => {
  const normalizedEmails = data.map(data => data.email.trim().toLowerCase());
  return new Set(normalizedEmails).size === normalizedEmails.length;
};
